/**
 * Risk Detail Page
 * POC implementation migrating risk-detail.html to React
 * Demonstrates dynamic field rendering, CRUD operations, and API integration
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FieldRenderer } from '../components/FieldRenderer';
import { RaciAssignments } from '../components/RaciAssignments';
import { LinkedArtefacts } from '../components/LinkedArtefacts';
import { DocumentManager } from '../components/DocumentManager';
import { RaiseArtefactModal } from '../components/RaiseArtefactModal';
import { FairAssessmentPanel } from '../components/FairAssessmentPanel';
import type { FairAssessment } from '../types/fair';
import { riskApi, configApi, peopleApi, assetsApi, departmentsApi, listApi, documentApi, linkedArtefactsApi, controlApi } from '../services/api';
import type { FieldConfig, FieldValues, Person, Asset, Department } from '../types/models';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { getMissingRequiredFields } from '../utils/fieldValidation';
import { computeRiskScores } from '../utils/riskScoring';

type DocumentListItem = {
  documentId: string;
  title?: string;
};

type Control = {
  controlId: string;
  fields?: Record<string, any>;
};

export const RiskDetail: React.FC = () => {
  const { riskId } = useParams<{ riskId: string }>();
  const [searchParams] = useSearchParams();
  const registerId = searchParams.get('registerId') || '';
  const navigate = useNavigate();
  
  const { getTenantId } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  // Local state for field values
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  // New risk, or the list's Edit action (?edit=true), both start in edit mode - otherwise
  // clicking Edit landed on the same read-only view as clicking anywhere else in the row, making
  // the action itself pointless.
  const [isEditing, setIsEditing] = useState(!riskId || searchParams.get('edit') === 'true');
  const [activeTab, setActiveTab] = useState<'details' | 'attachments' | 'raci' | 'artefacts'>('details');
  const [showRaiseModal, setShowRaiseModal] = useState(false);
  const [invalidFieldIds, setInvalidFieldIds] = useState<Set<string>>(new Set());

  // Fetch field configurations
  const { data: fieldConfigs = [], isLoading: isLoadingConfigs } = useQuery({
    queryKey: ['fieldConfigs', registerId],
    queryFn: () => configApi.getFieldConfigs(registerId),
    enabled: !!registerId,
    select: (data) => {
      if (!Array.isArray(data)) return data;
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      return data.map(sanitizeObject);
    },
  });

  // Fetch card definitions (card names)
  const { data: cardDefinitions = {}, isLoading: isLoadingCards } = useQuery<Record<string, string>>({
    queryKey: ['cardDefinitions', registerId],
    queryFn: () => configApi.getCardDefinitions(registerId),
    enabled: !!registerId,
    select: (data) => {
      if (!data || typeof data !== 'object') return data;
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      return sanitizeObject(data);
    },
  });

  // Fetch risk data (if editing existing)
  const { data: risk, isLoading: isLoadingRisk } = useQuery({
    queryKey: ['risk', riskId],
    queryFn: () => riskApi.getOne(riskId!),
    enabled: !!riskId,
    select: (data) => {
      if (!data) return data;
      
      // Deep sanitization helper
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        
        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        
        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      
      return sanitizeObject(data);
    },
  });

  // Fetch people for person dropdowns
  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
    select: (data) => {
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        
        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        
        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      
      return sanitizeObject(data);
    },
  });

  // Fetch assets for asset dropdowns
  const { data: assets = [] } = useQuery<Asset[]>({
    queryKey: ['assets'],
    queryFn: assetsApi.getAll,
    select: (data) => {
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        
        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        
        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      
      return sanitizeObject(data);
    },
  });

  // Fetch departments for the Department dropdown - was missing entirely, so that field always
  // rendered with zero options regardless of its own fieldType (see ControlDetail.tsx/
  // RegisterItemDetail.tsx, which already fetch and pass this the same way).
  const { data: departments = [] } = useQuery<Department[]>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
    select: (data) => {
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      return sanitizeObject(data);
    },
  });

  // Fetch threats for the FAIR panel's "Threat Community" dropdown
  const { data: threats = [] } = useQuery<any[]>({
    queryKey: ['threats'],
    queryFn: listApi.threats,
  });

  // Load risk data into form
  useEffect(() => {
    if (risk?.fields) {
      // Sanitize field values to extract strings from {value, label} objects
      const sanitizedFields: FieldValues = {};
      Object.keys(risk.fields).forEach((key) => {
        const val = risk.fields[key];
        if (val && typeof val === 'object' && 'value' in val) {
          sanitizedFields[key] = val.value;
        } else {
          sanitizedFields[key] = val;
        }
      });

      // `status` lives at the top level of the risk record (a sibling of `fields`,
      // not inside it), but is folded into fieldValues here too so it can be read
      // and updated alongside everything else - handleSave below pulls it back out
      // to the top level before saving.
      if ((risk as any).status) {
        sanitizedFields.status = (risk as any).status;
      }

      setFieldValues(sanitizedFields);
    }
  }, [risk]);

  // Procedure documents for Business Process dropdown
  // Fetch all documents from all document registers and filter by documentType
  const { data: procedureDocs = [] } = useQuery<DocumentListItem[]>({
    queryKey: ['procedureDocs'],
    queryFn: async () => {
      // Get all document registers
      const registers = await documentApi.getRegisters();
      
      // Fetch documents from all registers in parallel
      const documentsPromises = (registers || []).map((reg: any) =>
        documentApi.listByRegister(reg.registerId).catch(() => [])
      );
      const documentsArrays = await Promise.all(documentsPromises);
      
      // Flatten and filter for Procedure/Process types
      const allDocs = documentsArrays.flat();
      const filteredDocs = allDocs.filter((doc: any) => {
        const docType = String(doc?.documentType || '').toLowerCase();
        return docType === 'procedure' || docType === 'process';
      });
      
      return filteredDocs
        .map((d: any) => ({
          documentId: d?.documentId,
          title: d?.title || '',
        }))
        .filter((d: any) => d?.documentId);
    },
  });

  const procedureOptions = procedureDocs
    .map((d) => ({
      value: d.documentId,
      // The internal document id (e.g. "DOC-2026-042") used to be prefixed onto every label -
      // meaningless to a user picking a business process by name. Falls back to the id only for
      // the rare document with no title at all, so the option is never blank.
      label: d.title || d.documentId
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Single source of truth for each field's EFFECTIVE config (fieldType/options/isCalculated/
  // isEditable overrides), so what's rendered via FieldRenderer and what's validated by
  // getMissingRequiredFields can never drift apart. Mirrors, field-for-field, the inline
  // per-fieldId overlay that used to live in the JSX .map() below.
  const effectiveFieldConfigs = useMemo<FieldConfig[]>(() => {
    return (fieldConfigs as FieldConfig[])
      .filter((config) => config.isVisible)
      .map((config) => {
        const effective: FieldConfig = { ...config };

        // Business Process: document-driven dropdown of Procedure documents.
        if (config.fieldId === 'business_process') {
          effective.fieldType = 'procedure';
          effective.options = procedureOptions as any;
        }
        // Owner: people dropdown
        if (config.fieldId === 'owner') {
          effective.fieldType = 'person';
        }
        // Ensure calculated fields render read-only.
        if (config.fieldId === 'inherent_risk' || config.fieldId === 'residual_risk') {
          effective.fieldType = 'calculated';
          effective.isEditable = false;
          effective.isCalculated = true;
        }
        // Rich text fields per Standard Risk spec.
        if (config.fieldId === 'description' || config.fieldId === 'mitigation') {
          effective.fieldType = 'richtext';
        }

        // PRESERVED QUIRK (do not "fix"): in the original inline spread, this
        // isEditable assignment was the LAST key in the object literal, so it always
        // wins over the inherent_risk/residual_risk block's `isEditable: false` above
        // when isEditing is true - the effective isEditable for those two fields then
        // becomes whatever the raw fieldConfig says, not false. See summary for why
        // this has no visible effect today (fieldType 'calculated' ignores isEditable,
        // and isCalculated:true already excludes them from the required-field check).
        effective.isEditable = isEditing ? config.isEditable : false;

        return effective;
      });
  }, [fieldConfigs, isEditing, procedureOptions]);

  const effectiveFieldConfigsById = useMemo(() => {
    const map = new Map<string, FieldConfig>();
    effectiveFieldConfigs.forEach((config) => map.set(config.fieldId, config));
    return map;
  }, [effectiveFieldConfigs]);

  // True when residual_risk's current value includes at least one linked control whose
  // contribution came from assumedControlEffectiveness rather than a real, saved score.
  const [residualIsAssumed, setResidualIsAssumed] = useState(false);

  // Fetch linked controls for residual risk calculation
  const { data: linkedArtefactsData } = useQuery({
    queryKey: ['linkedArtefacts', riskId, 'risk'],
    queryFn: () => linkedArtefactsApi.getLinks(riskId!, 'risk'),
    enabled: !!riskId,
  });

  const linkedControls: Array<{ artefact_id: string }> = (linkedArtefactsData as any)?.links
    ? (linkedArtefactsData as any).links.filter((l: any) => l?.artefact_type === 'control')
    : [];
  const linkedControlIds = linkedControls.map((l) => l.artefact_id).filter(Boolean);

  const { data: linkedControlDetails = [] } = useQuery<Control[]>({
    queryKey: ['linkedControlDetails', linkedControlIds.join('|')],
    queryFn: async () => {
      const details = await Promise.all(linkedControlIds.map((id) => controlApi.getOne(id)));
      return (details || []).filter(Boolean);
    },
    enabled: linkedControlIds.length > 0,
  });

  // Fetch the linked asset (if any) so its criticality can boost inherent risk below.
  const linkedAssetId = typeof fieldValues.linked_asset === 'string' ? fieldValues.linked_asset : '';
  const { data: linkedAssetDetail } = useQuery<any>({
    queryKey: ['linkedAssetDetail', linkedAssetId],
    queryFn: () => assetsApi.getOne(linkedAssetId),
    enabled: !!linkedAssetId,
  });
  const linkedAssetCriticality = (linkedAssetDetail?.asset ?? linkedAssetDetail)?.criticality;

  // Calculate inherent/residual risk from likelihood/impact/linked-asset-criticality/linked
  // controls, via the same computeRiskScores also used by ControlDetail.tsx's post-save cascade
  // (see RUB-50672) so a Risk viewed here and a Risk recalculated from a Control save always
  // agree on the number.
  useEffect(() => {
    const { inherentRisk, residualRisk, residualIsAssumed } = computeRiskScores(
      fieldValues.likelihood,
      fieldValues.impact,
      linkedAssetCriticality,
      linkedControlDetails
    );
    setResidualIsAssumed(residualIsAssumed);
    if (fieldValues.inherent_risk !== inherentRisk || fieldValues.residual_risk !== residualRisk) {
      setFieldValues((prev) => ({
        ...prev,
        inherent_risk: inherentRisk,
        residual_risk: residualRisk,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues.likelihood, fieldValues.impact, linkedAssetCriticality, linkedControlDetails]);

  // Default Risk Response to "Accept" once inherent or residual risk lands at Medium or
  // Low - but only ever fills in a BLANK response. It never overwrites a value someone
  // (or a previous run of this same default) has already set, so it's a one-time suggestion
  // rather than something that fights a manual override on every recalculation. The risk's
  // status moves with it (existing "Accept"/"accepted" and "Mitigate"/"mitigated" risks in
  // this register are 100% aligned that way already), but only while status is still the
  // default "open" - if it's already been moved on to something else, that's left alone.
  useEffect(() => {
    if (fieldValues.risk_response) return;
    const isAcceptable = (value: unknown) => {
      const category = String(value ?? '').split('(')[0].trim().toLowerCase();
      return category === 'medium' || category === 'low';
    };
    if (isAcceptable(fieldValues.inherent_risk) || isAcceptable(fieldValues.residual_risk)) {
      setFieldValues((prev) => ({
        ...prev,
        risk_response: 'Accept',
        ...(!prev.status || prev.status === 'open' ? { status: 'accepted' } : null),
      }));
    }
  }, [fieldValues.inherent_risk, fieldValues.residual_risk, fieldValues.risk_response]);

  // Calculate next review date when last review date or frequency changes
  useEffect(() => {
    const lastReviewDate = fieldValues.last_review_date || fieldValues.lastReviewDate;
    const reviewFrequency = fieldValues.review_frequency || fieldValues.reviewFrequency || fieldValues.review_frequency_days;
    
    // Ensure both are strings for parsing
    const lastReviewStr = typeof lastReviewDate === 'string' ? lastReviewDate : '';
    const reviewFreqStr = typeof reviewFrequency === 'string' ? reviewFrequency : String(reviewFrequency || '');
    
    if (lastReviewStr && reviewFreqStr) {
      // Convert frequency text to days
      const frequencyMap: Record<string, number> = {
        'Monthly': 30,
        'Quarterly': 90,
        'Semi-Annually': 180,
        'Annually': 365,
        'Bi-Annually': 730,
      };
      
      let frequencyDays = 0;
      
      // Check if it's a text frequency or numeric days
      if (frequencyMap[reviewFreqStr]) {
        frequencyDays = frequencyMap[reviewFreqStr];
      } else if (!isNaN(parseInt(reviewFreqStr))) {
        frequencyDays = parseInt(reviewFreqStr);
      }
      
      if (frequencyDays > 0) {
        const lastDate = new Date(lastReviewStr);
        
        if (!isNaN(lastDate.getTime())) {
          const nextDate = new Date(lastDate);
          nextDate.setDate(nextDate.getDate() + frequencyDays);
          
          const nextReviewDateStr = nextDate.toISOString().split('T')[0];
          
          // Set all possible field name variations
          const currentNextReview = fieldValues.next_review_date || fieldValues.nextReviewDate || fieldValues.review_date || fieldValues.next_test_date;
          
          if (currentNextReview !== nextReviewDateStr) {
            setFieldValues((prev) => ({
              ...prev,
              next_review_date: nextReviewDateStr,
              nextReviewDate: nextReviewDateStr,
              review_date: nextReviewDateStr,
              next_test_date: nextReviewDateStr,
            }));
          }
        }
      }
    }
  }, [fieldValues.last_review_date, fieldValues.lastReviewDate, fieldValues.review_frequency, fieldValues.reviewFrequency, fieldValues.review_frequency_days]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      if (riskId) {
        return riskApi.update(riskId, data);
      } else {
        return riskApi.create(data);
      }
    },
    onSuccess: (savedRisk) => {
      // Invalidate the risks list so the register updates
      queryClient.invalidateQueries({ queryKey: ['list', '/risks', registerId] });
      queryClient.invalidateQueries({ queryKey: ['reports'] }); // gap analysis / reports
      
      // If creating a new risk, navigate to the detail page with the new ID
      if (!riskId && savedRisk?.riskId) {
        // Update URL to include the new risk ID
        window.location.href = `/risks/${savedRisk.riskId}?registerId=${registerId}`;
      } else {
        // For updates, just invalidate the current risk's query
        queryClient.invalidateQueries({ queryKey: ['risk', riskId] });
        setIsEditing(false);
      }
      
      toast.success('Risk updated.');
    },
    onError: (error: Error) => {
      console.error('Save error:', error);
      toast.error(`Failed to save risk: ${error.message}`);
    },
  });

  // Handle field change
  const handleFieldChange = (fieldId: string, value: any) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
    if (invalidFieldIds.has(fieldId)) {
      setInvalidFieldIds((prev) => {
        const next = new Set(prev);
        next.delete(fieldId);
        return next;
      });
    }
  };

  // Handle save
  const handleSave = () => {
    const missing = getMissingRequiredFields(effectiveFieldConfigs, fieldValues);
    if (missing.length > 0) {
      setInvalidFieldIds(new Set(missing));
      const names = effectiveFieldConfigs.filter((c) => missing.includes(c.fieldId)).map((c) => c.fieldName).join(', ');
      toast.error(`Please fill in required fields: ${names}`);
      return;
    }
    setInvalidFieldIds(new Set());

    // Filter out calculated and readonly fields
    const dataToSave = { ...fieldValues };

    fieldConfigs.forEach((config: FieldConfig) => {
      // Keep calculated fields: they drive list views/reports and are deterministic.
      // Drop non-editable non-calculated fields (system/read-only fields).
      if (!config.isEditable && !config.isCalculated) {
        delete dataToSave[config.fieldId];
      }
    });

    // `status` was folded into fieldValues for convenience (see the load effect above),
    // but the API expects it as a top-level sibling of `fields`, not nested inside it.
    const statusToSave = dataToSave.status;
    delete dataToSave.status;

    const payload: Record<string, any> = {
      registerId,
      tenantId: getTenantId(),
      fields: dataToSave,
    };
    if (statusToSave) {
      payload.status = statusToSave;
    }

    saveMutation.mutate(payload);
  };

  // Group fields by card
  const groupedFields = (fieldConfigs as FieldConfig[]).reduce((acc: Record<number, FieldConfig[]>, config: FieldConfig) => {
    const cardNum = config.cardNumber || 1;
    if (!acc[cardNum]) {
      acc[cardNum] = [];
    }
    acc[cardNum].push(config);
    return acc;
  }, {});

  // Card names from API (with fallback to default names)
  const defaultCardNames: Record<number, string> = {
    1: 'Basic Information',
    2: 'Assessment',
    3: 'Status & Tracking',
    4: 'Additional Details',
  };

  const cardNames: Record<number, string> = {
    ...defaultCardNames,
    ...Object.fromEntries(
      Object.entries(cardDefinitions).map(([key, value]) => [Number(key), value])
    ),
  };

  // Loading state
  if (isLoadingConfigs || isLoadingCards || (riskId && isLoadingRisk)) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      {/* Header */}
      <div className="np-page-head">
        <div className="d-flex align-items-center gap-3">
          <span className="np-icon-badge np-tone-primary flex-shrink-0">
            <i className="ri-alert-line"></i>
          </span>
          <div>
            <h1>{riskId ? 'Edit Risk' : 'New Risk'}</h1>
            {riskId && (
              <div className="d-flex align-items-center gap-2">
                <span className="np-chip np-tone-neutral np-tabular">{riskId}</span>
                {(fieldValues.risk_title || fieldValues.title || fieldValues.name) && (
                  <span className="np-sub">
                    {String(fieldValues.risk_title || fieldValues.title || fieldValues.name)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="np-page-actions">
          {!isEditing ? (
            <>
              <button
                className="np-btn np-btn-primary"
                onClick={() => { setIsEditing(true); setInvalidFieldIds(new Set()); }}
              >
                <i className="ri-edit-line"></i>
                Edit
              </button>
              {riskId && (
                <button
                  className="np-btn np-btn-ghost"
                  onClick={() => setShowRaiseModal(true)}
                  title="Raise Issue or Incident against this risk"
                >
                  <i className="ri-add-circle-line"></i>
                  Raise
                </button>
              )}
              <button
                className="np-btn np-btn-ghost"
                onClick={() => window.history.back()}
              >
                <i className="ri-arrow-left-line"></i>
                Back
              </button>
            </>
          ) : (
            <>
              <button
                className="np-btn np-btn-primary"
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? (
                  <>
                    <span className="spinner-border spinner-border-sm"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    Save
                  </>
                )}
              </button>
              {riskId && (
                <button
                  className="np-btn np-btn-ghost"
                  onClick={() => {
                    setIsEditing(false);
                    setInvalidFieldIds(new Set());
                    if (risk?.fields) {
                      setFieldValues(risk.fields);
                    }
                  }}
                >
                  <i className="ri-close-line"></i>
                  Cancel
                </button>
              )}
              <button
                className="np-btn np-btn-ghost"
                onClick={() => {
                  // Invalidate risks list to ensure register reloads
                  queryClient.invalidateQueries({ queryKey: ['list', '/risks', registerId] });
                  // Navigate back to register
                  navigate(`/risks?registerId=${registerId}`);
                }}
              >
                <i className="ri-arrow-left-line"></i>
                Back
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Tab navigation ── */}
      <div className="np-tabs" style={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'details' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('details')}
        >
          <i className="ri-alert-line"></i>Risk Details
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'attachments' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('attachments')}
        >
          <i className="ri-attachment-2"></i>Attachments
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'raci' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('raci')}
        >
          <i className="ri-team-line"></i>RACI
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'artefacts' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('artefacts')}
        >
          <i className="ri-links-line"></i>Linked Artefacts
        </button>
      </div>

      {activeTab === 'details' && (
        <>
      {/* Dynamic Field Cards */}
      <div className="row">
        {Object.keys(groupedFields)
          .sort((a, b) => Number(a) - Number(b))
          .map((cardNum) => {
            const cardFields = groupedFields[Number(cardNum)]
              .filter((config: FieldConfig) => config.isVisible)
              .sort((a: FieldConfig, b: FieldConfig) => (a.orderIndex || 0) - (b.orderIndex || 0));

            if (cardFields.length === 0) return null;

            return (
              <div key={cardNum} className="col-12 mb-4">
                <div className="np-card">
                  <div className="np-card-head">
                    <h2>
                      {cardNames[Number(cardNum)] || `Card ${cardNum}`}
                    </h2>
                  </div>
                  <div className="np-card-body">
                    {cardFields.map((config: FieldConfig) => (
                      <React.Fragment key={config.fieldId}>
                        <FieldRenderer
                          config={effectiveFieldConfigsById.get(config.fieldId) ?? config}
                          value={fieldValues[config.fieldId]}
                          onChange={handleFieldChange}
                          people={people}
                          assets={assets}
                          departments={departments}
                          error={invalidFieldIds.has(config.fieldId) ? 'This field is required.' : undefined}
                        />
                        {config.fieldId === 'residual_risk' && residualIsAssumed && (
                          <div className="np-hint mb-2">
                            <i className="ri-error-warning-line me-1"></i>
                            Includes a linked control not yet independently scored — using an estimate based on its maturity/implementation status, not a saved assessment.
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      <FairAssessmentPanel
        value={fieldValues.fair_assessment as FairAssessment | null | undefined}
        onChange={(next) => handleFieldChange('fair_assessment', next)}
        onApplyToRisk={(likelihood, impact) => {
          handleFieldChange('likelihood', likelihood);
          handleFieldChange('impact', impact);
        }}
        readOnly={!isEditing}
        assets={assets}
        threats={threats}
      />

      </>
      )}

      {activeTab === 'attachments' && (
        <div className="row">
          {riskId ? (
            <DocumentManager entityType="risk" entityId={riskId} readOnly={!isEditing} />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-attachment-2"></i> Attachments</h2>
                </div>
                <div className="np-card-body">
                  <p className="np-sub mb-0">Available after you save this risk.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'raci' && (
        <div className="row">
          {riskId ? (
            <RaciAssignments artefactId={riskId} artefactType="risk" readOnly={!isEditing} />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-team-line"></i> RACI Assignments</h2>
                </div>
                <div className="np-card-body">
                  <p className="np-sub mb-0">Available after you save this risk.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'artefacts' && (
        <div className="row">
          {riskId && registerId ? (
            <LinkedArtefacts artefactId={riskId} artefactType="risk" artefactRegister={registerId} registerId={registerId} readOnly={!isEditing} />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-links-line"></i> Linked Artefacts</h2>
                </div>
                <div className="np-card-body">
                  <p className="np-sub mb-0">Available after you save this risk.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}      

      {showRaiseModal && riskId && (
        <RaiseArtefactModal
          riskId={riskId}
          riskTitle={String(fieldValues.risk_title || fieldValues.title || fieldValues.name || '')}
          riskRegister={registerId}
          allowedTypes={['issue', 'incident']}
          onClose={() => setShowRaiseModal(false)}
        />
      )}
    </div>
  );
};
